import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  BookOpen, 
  Terminal, 
  Award, 
  Briefcase, 
  FileText, 
  Users, 
  Settings, 
  ChevronRight, 
  ChevronLeft,
  ShieldCheck,
  Video,
  LogOut,
  UserCheck
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ThemeSwitcher } from '@/components/dashboard/ThemeSwitcher';

interface NavItem {
  id: string;
  label: string;
  sublabel: string;
  path: string;
  icon: any;
  roleRequired?: 'student' | 'teacher' | 'recruiter' | 'admin';
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Главный дашборд', sublabel: 'Обзор и аналитика', path: '/', icon: Home },
  { id: 'ide', label: 'Среда разработки (IDE)', sublabel: 'Редактор кода и терминал', path: '/ide', icon: Terminal },
  { id: 'assessment', label: 'Оценка UzDigComp-AI', sublabel: '7 сфер и 42 индикатора', path: '/assessment', icon: Award },
  { id: 'interview', label: 'Симулятор собеседования', sublabel: 'Голосовое и текстовое', path: '/interview', icon: Briefcase },
  { id: 'videohub', label: 'Видеопрактикум', sublabel: 'Интерактивные видеокурсы', path: '/videohub', icon: Video },
  { id: 'profile', label: 'Личный кабинет', sublabel: 'Профиль компетенций', path: '/profile', icon: Users },
];

interface AppSidebarProps {
  onOpenAdmin?: () => void;
  onOpenTeacher?: () => void;
}

export default function AppSidebar({ onOpenAdmin, onOpenTeacher }: AppSidebarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, metadata, logout, isModerator, isTeacher } = useAuth();

  return (
    <>
      <aside 
        className={`fixed top-0 left-0 bottom-0 z-50 transition-all duration-300 flex flex-col justify-between border-r border-white/10 bg-[#0d0f17]/95 backdrop-blur-2xl ${
          isExpanded ? 'w-72 shadow-2xl shadow-black/80' : 'w-20'
        }`}
      >
        <div className="flex flex-col">
          <div className="h-20 flex items-center justify-between px-5 border-b border-white/10">
            <div 
              onClick={() => navigate('/')} 
              className="flex items-center gap-3 cursor-pointer overflow-hidden"
            >
              <div className="w-10 h-10 rounded-xl bg-white text-[#090a0f] flex items-center justify-center font-black text-sm flex-shrink-0 shadow-lg shadow-white/15">
                SP
              </div>
              {isExpanded && (
                <div className="flex flex-col whitespace-nowrap overflow-hidden">
                  <span className="font-extrabold text-sm uppercase tracking-wider text-white">Study Platform</span>
                  <span className="text-[10px] text-white/50 uppercase tracking-widest">Ecosystem Core</span>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              title={isExpanded ? 'Свернуть меню' : 'Развернуть меню'}
            >
              {isExpanded ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
          </div>

          <nav className="p-3 space-y-1.5 mt-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3.5 px-3.5 py-3 rounded-2xl transition-all text-left ${
                    isActive
                      ? 'glass-btn-primary shadow-lg shadow-white/15'
                      : 'hover:bg-white/[0.06] text-white/70 hover:text-white'
                  }`}
                  title={!isExpanded ? item.label : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-[#07030e]' : 'text-white'}`} />
                  {isExpanded && (
                    <div className="flex flex-col min-w-0">
                      <span className={`text-xs font-bold leading-tight ${isActive ? 'text-[#07030e]' : 'text-white'}`}>
                        {item.label}
                      </span>
                      <span className={`text-[10px] truncate ${isActive ? 'text-[#07030e]/70' : 'text-white/40'}`}>
                        {item.sublabel}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}

            {(isModerator || isTeacher) && onOpenAdmin && (
              <button
                type="button"
                onClick={isModerator ? onOpenAdmin : onOpenTeacher}
                className={`w-full flex items-center gap-3.5 px-3.5 py-3 rounded-2xl transition-all text-left bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20`}
                title={!isExpanded ? 'Панель управления' : undefined}
              >
                <ShieldCheck className="w-5 h-5 flex-shrink-0 text-amber-400" />
                {isExpanded && (
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold leading-tight text-amber-300">
                      {isModerator ? 'Админ-панель' : 'Панель учителя'}
                    </span>
                    <span className="text-[10px] text-amber-400/60 truncate">
                      Управление системой
                    </span>
                  </div>
                )}
              </button>
            )}
          </nav>
        </div>

        <div className="p-4 border-t border-white/10 space-y-3">
          <div className={`flex items-center ${isExpanded ? 'justify-between' : 'justify-center'}`}>
            {isExpanded && <span className="text-[10px] uppercase font-bold text-white/50 tracking-wider">Тема</span>}
            <ThemeSwitcher />
          </div>

          {user ? (
            <div className="flex items-center justify-between gap-3">
              <div 
                onClick={() => navigate('/profile')}
                className="flex items-center gap-3 cursor-pointer overflow-hidden flex-1"
              >
                <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center font-bold text-xs text-white flex-shrink-0">
                  <UserCheck className="w-4 h-4" />
                </div>
                {isExpanded && (
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold text-white truncate">{metadata?.fullName || 'Пользователь'}</span>
                    <span className="text-[10px] text-white/50 font-mono uppercase">{metadata?.role || 'Студент'}</span>
                  </div>
                )}
              </div>

              {isExpanded && (
                <button
                  type="button"
                  onClick={() => logout()}
                  className="p-2 rounded-xl text-white/50 hover:text-rose-400 hover:bg-white/5 transition-colors flex-shrink-0"
                  title="Выйти"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className={`w-full py-2.5 rounded-xl text-xs uppercase tracking-wider ${
                isExpanded ? 'glass-btn-primary' : 'p-2 flex justify-center text-white'
              }`}
            >
              {isExpanded ? 'Вход в аккаунт' : <UserCheck className="w-5 h-5" />}
            </button>
          )}
        </div>
      </aside>

      {/* Backdrop overlay for small screens when sidebar is open */}
      {isExpanded && (
        <div 
          onClick={() => setIsExpanded(false)}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
        />
      )}
    </>
  );
}

